// Curtains — the three commands, and how long the motor runs for.
//
// ════════════════════════════════════════════════════════════════════════════
// A CURTAIN BUTTON IS MOMENTARY. The motor is energised, runs for a set number
// of seconds, and is switched off again; there is no "open" position to hold.
// So a press is a *timed run*, and something has to own the clock.
//
// ⚠️ THE DEVICE'S OWN CLOCK IS NOT USABLE AS THAT SOMETHING, and this is the
// whole reason the app runs one. Read out of the firmware:
//
//   • `OtaForTuya.ino:4016` — the travel time is EEPROM[33]/[34], clamped 3-30 s,
//     default 3, loaded only for DeviceType 13/37. **Nothing writes it.** There
//     is no MQTT command, no endpoint and no UI that can set it, so every
//     deployed unit is running the 3-second default it was born with.
//   • `OtaForSwitching` / `…NewDesign` (24 of the 27 deployed curtains) have no
//     travel timer at all — they hand the Tuya DP straight to the MCU and let
//     the MCU decide when to stop.
//
// The app therefore times the run and sends STOPCURTAIN itself at the end. That
// makes one configurable number govern every curtain in the estate regardless of
// which of the three firmwares it happens to run.
//
// ⚠️ IT IS A CEILING SHARED WITH THE HARDWARE, NOT SOLE CONTROL. A relay curtain
// on `OtaForTuya` still stops itself after ITS 3 seconds, so a 12-second setting
// gives 3 seconds of travel and 9 of the app counting down over a stopped motor.
// Whichever limit is shorter wins, and the app cannot see the other one. Setting
// a time here is safe in the sense that it can only ever stop the motor sooner
// than the hardware would; it cannot make it run longer.
// ════════════════════════════════════════════════════════════════════════════
//
// ── Where the times live ───────────────────────────────────────────────────
// `OutputDetails.KeyPressOffAfter`, on the curtain's own two output rows:
//
//     Output 1  ->  open  travel seconds
//     Output 2  ->  close travel seconds
//
// Per-output, which is what the two directions need — a heavy curtain does not
// take the same time each way. It is an existing `int NOT NULL DEFAULT 0` column
// on rows these devices already have (named "Curtain Open" / "Curtain Close" in
// the field), writable through the endpoint `buttonSettings.js` already uses.
// No schema change, no new endpoint.
//
// ⚠️ IT IS UNUSED EVERYWHERE ELSE, WHICH IS WHY IT IS SAFE. Checked before
// choosing it: 0 on all 4,555 rows in the estate, and not read by any of the
// three firmware sketches. If a future firmware starts reading this column,
// these values are what it will find.
//
// ⚠️ ZERO MEANS "NOT SET", NOT "ZERO SECONDS". The column defaults to 0 and the
// minimum real value is 1, so 0 is read as "never configured" and falls back to
// DEFAULT_TRAVEL_S. A curtain that ran for 0 seconds would look like a dead
// relay, so this distinction is not cosmetic.

import { getOutputs } from '../api/api';
import { updateOutputDetails } from './buttonSettings';

// ── The wire ───────────────────────────────────────────────────────────────
// Uppercase, no prefix, sent with the ordinary `publishCommand` envelope. All
// three firmwares accept four spellings each and upper-case before matching
// (`OtaForSwitching.ino:1063`), so case does not matter — these are the Flutter
// app's spellings and the ones the MQTT console presets use.
export const OPEN_CURTAIN = 'OPENCURTAIN';
export const STOP_CURTAIN = 'STOPCURTAIN';
export const CLOSE_CURTAIN = 'CLOSECURTAIN';

/** Which output row carries which direction's time. */
export const OUTPUT_OPEN = 1;
export const OUTPUT_CLOSE = 2;

// ── Which devices are driven this way ──────────────────────────────────────
//
// A **sliding gate is a curtain**, mechanically and on the wire. The firmware
// says so itself at `OtaForTuya.ino:6039`, where DeviceType 37 accepts the
// curtain words alongside its own: *"Accepts gate words and the curtain words
// too, because the app presents the gate with the same UI as a curtain."* Same
// two relays, same travel timers — `OtaForTuya.ino:4014` loads EEPROM[33]/[34]
// for `DeviceType == 13 || DeviceType == 37` and says the two types never
// coexist.
//
// ⚠️ SO THE APP SENDS THE CURTAIN WORDS TO A GATE, not OPENGATE/CLOSEGATE. Both
// work on `OtaForTuya`, but only the curtain words are recognised by the other
// two sketches, which have no gate branch at all. One vocabulary that every
// firmware understands beats a branch on a firmware the app cannot see.
export const SLIDING_GATE_ID = 37;

/*
 * ⚠️ 78 AND 79 ARE DELIBERATELY NOT HERE. "Curtain And Two Touch" (78) and "Two
 * Curtains" (79) have Outputs 4: they are a curtain plus two switches, and two
 * curtains on one board. One Open/Stop/Close would drive the first pair and
 * silently ignore the rest, which is worse than the generic output UI they get
 * now. There is not one of either in the field, so nothing is waiting on it.
 */
export const CURTAIN_DEVICE_IDS = [13, 51, SLIDING_GATE_ID];

export const isCurtainDevice = (deviceId) => CURTAIN_DEVICE_IDS.includes(Number(deviceId));

/**
 * What to call it on screen. The controls are identical; the words are not, and
 * "Open Curtain" on a driveway gate is the kind of label that makes an operator
 * doubt they are on the right device.
 */
export const curtainNoun = (deviceId) => (Number(deviceId) === SLIDING_GATE_ID ? 'Gate' : 'Curtain');

// ── The range ──────────────────────────────────────────────────────────────
export const MIN_TRAVEL_S = 1;
export const MAX_TRAVEL_S = 20;

/*
 * What an unconfigured curtain runs for.
 *
 * 3 seconds, because that is the firmware's own default (`OtaForTuya.ino:4018`)
 * and matching it means switching the app to timed runs changes nothing for a
 * curtain nobody has configured. Picking a different number here would silently
 * alter the behaviour of every existing installation on the day it shipped.
 */
export const DEFAULT_TRAVEL_S = 3;

/**
 * Force a value into the range, treating 0/blank/nonsense as unconfigured.
 *
 * ⚠️ CLAMPS RATHER THAN REJECTS. A stored 30 — the old firmware's ceiling was
 * 30, and a hand-edited row can hold anything — must not become a 30-second run
 * against a motor whose installer asked for 20. Out of range in either direction
 * comes back inside it.
 */
export function clampTravel(seconds) {
  const n = Math.round(Number(seconds));
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_TRAVEL_S;
  if (n < MIN_TRAVEL_S) return MIN_TRAVEL_S;
  if (n > MAX_TRAVEL_S) return MAX_TRAVEL_S;
  return n;
}

/** True when the row holds a real setting rather than the column's default. */
export const isConfigured = (seconds) => Number(seconds) > 0;

// Matched on the Output NUMBER, not array position — the rows come back in
// whatever order the query gives, and position-matching quietly returns the
// other direction's time. Same rule as buttonSettings.rowForOutput.
const rowFor = (rows, output) => (rows || []).find(
  (r) => Number(r.Output) === Number(output),
);

/**
 * The configured travel times for one curtain.
 *
 * -> { openSeconds, closeSeconds, openSet, closeSet, singleOutput }
 *
 * `*Set` says whether the number came from the row or from the default, because
 * a settings form must not present a fallback as if somebody had chosen it.
 * A device whose outputs cannot be read falls back to the defaults rather than
 * throwing: a curtain that still moves for 3 seconds is better than a screen
 * with no buttons.
 *
 * ⚠️ `singleOutput` EXISTS BECAUSE THE SLIDING GATE HAS ONLY ONE OUTPUT ROW.
 * `DeviceType 37` is `Outputs 1` in the catalogue, so its `OutputDetails` holds
 * a single row called "S1" — while the firmware drives TWO relays through
 * `GateDrive()` and honours two separate travel times. The catalogue and the
 * firmware disagree, and the app cannot fix that from here.
 *
 * The consequence had to be handled rather than ignored: a write to Output 2 on
 * such a device updates NO rows and reports success, so a close time saved
 * against a gate would silently vanish and read back as the default forever.
 * With one row, one time governs both directions — which is at least true, and
 * the dialog says so.
 */
export async function readCurtainTimes(uniqueNumber) {
  let rows = null;
  try {
    const res = await getOutputs(uniqueNumber);
    rows = Array.isArray(res?.data) ? res.data : [];
  } catch (e) {
    rows = null;
  }
  // A read FAILURE is not "one output" — that distinction decides whether the
  // dialog offers one field or two, and getting it wrong would hide the close
  // time from every curtain the moment the network hiccuped.
  const singleOutput = !!rows && rows.length > 0 && !rowFor(rows, OUTPUT_CLOSE);

  const rawOpen = rowFor(rows, OUTPUT_OPEN)?.KeyPressOffAfter;
  const rawClose = singleOutput ? rawOpen : rowFor(rows, OUTPUT_CLOSE)?.KeyPressOffAfter;
  return {
    openSeconds: clampTravel(rawOpen),
    closeSeconds: clampTravel(rawClose),
    openSet: isConfigured(rawOpen),
    closeSet: isConfigured(rawClose),
    singleOutput,
  };
}

/**
 * Save both times. -> { ok, error }
 *
 * Two writes, one per output row, because they are two rows — there is no
 * endpoint that takes both. They are awaited together and a failure of either
 * fails the save, since "open saved, close did not" is the state nobody could
 * work out from the screen afterwards.
 *
 * ⚠️ Pass `singleOutput` for a device with only Output 1 (see above). Without
 * it the close write goes to a row that does not exist, which the endpoint
 * reports as a success.
 */
export async function saveCurtainTimes(uniqueNumber, { openSeconds, closeSeconds, singleOutput }) {
  const open = clampTravel(openSeconds);
  const close = singleOutput ? open : clampTravel(closeSeconds);
  try {
    const writes = [updateOutputDetails(uniqueNumber, OUTPUT_OPEN, { KeyPressOffAfter: open })];
    if (!singleOutput) {
      writes.push(updateOutputDetails(uniqueNumber, OUTPUT_CLOSE, { KeyPressOffAfter: close }));
    }
    await Promise.all(writes);
    return { ok: true, openSeconds: open, closeSeconds: close };
  } catch (e) {
    return { ok: false, error: 'Could not save the travel times. Check your connection and try again.' };
  }
}
